/**
 * AITemplateGenerator Component
 * 
 * Column 1 of CustomRoundModal
 * Allows users to configure and generate AI-powered interview round templates
 */

import React, { useMemo } from "react";
import { 
  Typography, 
  Button, 
  Space,
  Alert 
} from "antd";
import { RobotOutlined, ThunderboltOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { AITemplateGeneratorProps } from "../types";
import { SHARED_STYLES, COLUMN_STYLES, ColumnStatus } from "../constants";
import SkillsSelector from "./SkillsSelector";
import ConfigurationPanel from "./ConfigurationPanel";
import GenerationProgress from "./GenerationProgress";
import ColumnProgressIndicator from "./ColumnProgressIndicator";

const { Text } = Typography;

const AITemplateGenerator: React.FC<AITemplateGeneratorProps> = ({
  // Skills props
  availableSkills,
  selectedSkills,
  onSkillToggle,
  onSelectAllSkills,
  onClearAllSkills,
  customSkillInput,
  onCustomSkillInputChange,
  onAddCustomSkill,
  
  // Question configuration props
  selectedQuestionType,
  onQuestionTypeChange,
  questionStyles,
  filteredQuestionStyles,
  selectedQuestionStyle,
  onQuestionStyleChange,
  selectedDifficulty,
  onDifficultyChange,
  
  // Generation state props
  isLoadingStyles,
  isGeneratingTemplate,
  isTemplateGeneratorReady,
  onGenerateTemplate,
  
  // Generation progress props
  generationStep,
  generationSteps,
  
  // Settings mode
  settingsOnlyMode = false,
}) => {
  // Compute missing fields for helper text
  const missingFields = useMemo(() => {
    const missing: string[] = [];
    if (selectedSkills.length === 0) missing.push("Skills");
    if (!selectedQuestionType) missing.push("Question Type");
    if (!selectedQuestionStyle) missing.push("Question Style");
    if (!selectedDifficulty) missing.push("Difficulty Level");
    return missing;
  }, [selectedSkills, selectedQuestionType, selectedQuestionStyle, selectedDifficulty]);

  // Generate helper text based on missing fields
  const helperText = useMemo(() => {
    if (missingFields.length === 0) return null;
    if (missingFields.length === 1) {
      return `Select ${missingFields[0]} to continue`;
    }
    if (missingFields.length === 4) {
      return "Complete all fields above to generate template";
    }
    // Format: "Select Skills, Question Type, and Difficulty Level"
    const lastField = missingFields[missingFields.length - 1];
    const otherFields = missingFields.slice(0, -1).join(", ");
    return `Select ${otherFields} and ${lastField}`;
  }, [missingFields]);

  // Compute column progress status
  const columnStatus: ColumnStatus = useMemo(() => {
    // Completed when template generator is ready (all fields filled)
    if (isTemplateGeneratorReady) return "completed";
    // In progress when at least one field is configured
    if (selectedSkills.length > 0 || selectedQuestionType || selectedQuestionStyle || selectedDifficulty) {
      return "inProgress";
    }
    // Todo when nothing is configured
    return "todo";
  }, [isTemplateGeneratorReady, selectedSkills, selectedQuestionType, selectedQuestionStyle, selectedDifficulty]);

  if (settingsOnlyMode) {
    return (
      <div
        style={{
          ...COLUMN_STYLES.base,
          ...COLUMN_STYLES.column1,
        }}
      >
        {/* Header with Progress Indicator */}
        <div style={COLUMN_STYLES.headerWrapper}>
          <div style={COLUMN_STYLES.header}>
            <Text strong style={{ fontSize: 16 }}>
              <RobotOutlined style={{ marginRight: 8, color: SHARED_STYLES.primaryColor }} />
              Round Settings
            </Text>
            <Text type="secondary" style={{ display: "block", fontSize: SHARED_STYLES.fontSizeSm }}>
              Settings only mode
            </Text>
          </div>
          <ColumnProgressIndicator status="completed" />
        </div>

        {/* Content */}
        <div
          className="custom-round-modal-scrollable"
          style={COLUMN_STYLES.scrollableContent}
        >
          <Alert
            type="info"
            message="Settings Mode"
            description="Only scoring and qualification settings can be modified for this round."
            showIcon
          />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        ...COLUMN_STYLES.base,
        ...COLUMN_STYLES.column1,
      }}
    >
      {/* Header with Progress Indicator */}
      <div style={COLUMN_STYLES.headerWrapper}>
        <div style={COLUMN_STYLES.header}>
          <Text strong style={{ fontSize: 16 }}>
            <RobotOutlined style={{ marginRight: 8, color: SHARED_STYLES.primaryColor }} />
            AI Template Generator
          </Text>
          <Text type="secondary" style={{ display: "block", fontSize: SHARED_STYLES.fontSizeSm }}>
            Configure and generate templates
          </Text>
        </div>
        <ColumnProgressIndicator status={columnStatus} />
      </div>

      {/* Scrollable Content */}
      <div
        className="custom-round-modal-scrollable"
        style={COLUMN_STYLES.scrollableContent}
      >
        {/* Show progress stepper when generating or just completed */}
        {/* {(isGeneratingTemplate || generationStep >= 0) && (
          <div style={{ marginBottom: SHARED_STYLES.marginMd }}>
            <GenerationProgress
              currentStep={generationStep}
              steps={generationSteps}
              isGenerating={isGeneratingTemplate}
            />
          </div>
        )} */}

        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {/* Skills Section */}
          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: SHARED_STYLES.fontSizeSm }}>
              Skills
            </Text>
            <SkillsSelector
              availableSkills={availableSkills}
              selectedSkills={selectedSkills}
              onSkillToggle={onSkillToggle}
              onSelectAll={onSelectAllSkills}
              onClearAll={onClearAllSkills}
              customSkillInput={customSkillInput}
              onCustomSkillInputChange={onCustomSkillInputChange}
              onAddCustomSkill={onAddCustomSkill}
              disabled={isGeneratingTemplate}
            />
          </div>

          {/* Configuration Section */}
          <ConfigurationPanel
            selectedQuestionType={selectedQuestionType}
            onQuestionTypeChange={onQuestionTypeChange}
            filteredQuestionStyles={filteredQuestionStyles}
            selectedQuestionStyle={selectedQuestionStyle}
            onQuestionStyleChange={onQuestionStyleChange}
            selectedDifficulty={selectedDifficulty}
            onDifficultyChange={onDifficultyChange}
            isLoadingStyles={isLoadingStyles}
            disabled={isGeneratingTemplate}
          />
        </Space>
      </div>

      {/* Footer with Generate Button */}
      <div
        style={{
          ...COLUMN_STYLES.footer,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <Button
          type="primary"
          size="large"
          icon={<ThunderboltOutlined />}
          onClick={onGenerateTemplate}
          loading={isGeneratingTemplate}
          disabled={!isTemplateGeneratorReady}
          style={{ width: "100%" }}
        >
          {isGeneratingTemplate ? "Generating..." : "Generate Template"}
        </Button>
        
        {/* Helper text showing missing fields */}
        {!isTemplateGeneratorReady && !isGeneratingTemplate && helperText && (
          <Text 
            type="danger" 
            style={{ 
              fontSize: SHARED_STYLES.fontSizeSm, 
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
            }}
          >
            <InfoCircleOutlined />
            {helperText}
          </Text>
        )}
      </div>
    </div>
  );
};

export default AITemplateGenerator;

